/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import moment from 'moment'
import { calendarTheme } from './calendar_theme'
import { getMonthGridDays } from './calendar_utils'

const MonthMini = ({ monthStart, occurrencesByDate, onDrillToMonth }) => {
  const monthDays = useMemo(() => getMonthGridDays(monthStart), [monthStart])
  const weekdayLabels = useMemo(() => monthDays.slice(0, 7).map((day) => day.format('dd').charAt(0)), [monthDays])
  const monthKey = monthStart.format('YYYY-MM')

  return (
    <Box
      sx={{
        border: `1px solid ${calendarTheme.colors.gridBorder}`,
        borderRadius: '12px',
        backgroundColor: calendarTheme.colors.background,
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => onDrillToMonth(monthStart)}
        data-testid={`dynamic-discounts-calendar-year-month-${monthStart.format('YYYY-MM')}`}
        aria-label={`View ${monthStart.format('MMMM YYYY')}`}
        sx={{
          width: '100%',
          border: 'none',
          cursor: 'pointer',
          backgroundColor: '#fafafa',
          borderBottom: `1px solid ${calendarTheme.colors.gridBorder}`,
          textAlign: 'left',
          px: 1.25,
          py: 0.75,
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: calendarTheme.colors.headingText }}>
          {monthStart.format('MMMM')}
        </Typography>
      </Box>

      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
          {weekdayLabels.map((label, index) => (
            <Typography
              key={`label-${index}`}
              sx={{ fontSize: '9px', fontWeight: 600, color: calendarTheme.colors.mutedText, textAlign: 'center' }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: '2px' }}>
          {monthDays.map((day) => {
            const dayKey = day.format('YYYY-MM-DD')
            const isCurrentMonth = day.isSame(monthStart, 'month')
            const isToday = day.isSame(moment(), 'day')
            const hasEvents = Boolean(occurrencesByDate?.[dayKey]?.length)

            return (
              <Box
                key={dayKey}
                component="button"
                type="button"
                onClick={() => onDrillToMonth(day)}
                data-testid={`dynamic-discounts-calendar-year-day-${monthKey}-${dayKey}`}
                aria-label={`View month of ${day.format('MMMM D, YYYY')}`}
                sx={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '2px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isToday ? calendarTheme.defaultCardColor : 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: isToday ? 700 : 500,
                      color: isToday
                        ? calendarTheme.colors.eventBackground
                        : isCurrentMonth
                          ? calendarTheme.colors.subheadingText
                          : '#d4d4d8',
                    }}
                  >
                    {day.format('D')}
                  </Typography>
                </Box>
                <Box
                  data-testid={`dynamic-discounts-calendar-year-day-dot-${monthKey}-${dayKey}`}
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    backgroundColor: hasEvents ? calendarTheme.defaultCardColor : 'transparent',
                  }}
                />
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export const YearGrid = ({ yearStart, occurrencesByDate, onDrillToMonth }) => {
  const months = useMemo(() => (
    Array.from({ length: 12 }, (_, index) => yearStart.clone().add(index, 'months').startOf('month'))
  ), [yearStart])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {months.map((monthStart) => (
          <MonthMini
            key={monthStart.format('YYYY-MM')}
            monthStart={monthStart}
            occurrencesByDate={occurrencesByDate}
            onDrillToMonth={onDrillToMonth}
          />
        ))}
      </Box>
    </Box>
  )
}
